import React, { useCallback, useContext, useMemo, useState } from 'react';
import { Alert } from '@mui/material';
import { Prompt, useHistory } from 'react-router-dom';
import { inspectRouteSessionSlug } from '../../../../constants/slugs';
import Table from '../../../../custom-components/Table/Table';
import DefaultToolbar from '../../../../custom-components/Table/DefaultToolbar';
import TitledPage from '../../../../custom-components/page-layout/titled-page/TitledPage';
import { baseTableSx, baseToolbarProps } from '../../../../custom-components/Table/Table.helpers';
import DocumentViewerDialog from '../../FunctionalApps/inspect/processed-fluids-route-session/DocumentViewerDialog';
import startDownload from '../../FunctionalApps/inspect/processed-fluids-route-session/startDownload';
import getFileURL from '../../../../repository/getFileURL';
import RedirectGuard from '../../../../split/RedirectGuard';
import { useConfirmationDialog } from '../../../../hooks/useConfirmationDialog';
import { NotificationContext } from '../../../../NotificationContext';
import LocalizationContext from '../../../../localization/LocalizationContext';
import { addPageTracking, userHasPermission } from '../../../../../utils';
import { useGenerateDeliveryDocument, useGetDeliveryDocuments } from '../../../../data/generic-queries/delivery-documents';
import DeliveryDocumentFilters, { getDateRangeError, getDefaultFilters, toQueryParams } from './DeliveryDocumentFilters';
import getDeliveryDocumentColumns from './deliveryDocumentColumns';
import useDeliveryReportsDownload from './useDeliveryReportsDownload';
import DeliveryReportsDownloadDialog from './DeliveryReportsDownloadDialog';
import DeliveryReportsFolderDialog from './DeliveryReportsFolderDialog';
import DeliveryReportHistoryDialog from './DeliveryReportHistoryDialog';
import { DELIVERY_DOCUMENTS_PERMISSION, MAX_ZIP_DOCUMENTS, deliveryDocumentFilename, deliveryDocumentVersionFilename } from './constants';

addPageTracking('delivery-documents');

const documentTitle = (row) => `Delivery Report - ${row.BOL ?? row.manifest_number ?? ''}`;

const matchesSearch = (row, search) => !search || (row.BOL ?? '').toLowerCase().includes(search.trim().toLowerCase());

const DeliveryDocumentsPage = () => {
    const { showSnackbar } = useContext(NotificationContext);
    const localization = useContext(LocalizationContext);
    const history = useHistory();
    const { ConfirmationDialog, confirmAsync } = useConfirmationDialog();
    const [filters, setFilters] = useState(() => getDefaultFilters());
    const [selectionModel, setSelectionModel] = useState([]);
    const [viewing, setViewing] = useState(null);
    const [versionsFor, setVersionsFor] = useState(null);
    const [folderDialogOpen, setFolderDialogOpen] = useState(false);
    const [layout, setLayout] = useState([]);

    const dateError = getDateRangeError(filters);
    const params = useMemo(() => (dateError ? null : toQueryParams(filters)), [filters, dateError]);
    const { data: rows = [], isLoading, isFetching, isError, refetch } = useGetDeliveryDocuments(params, { enabled: !dateError });
    const { mutateAsync: generate, isLoading: generating } = useGenerateDeliveryDocument();
    const download = useDeliveryReportsDownload();
    const { downloading } = download;

    const visibleRows = useMemo(() => rows.filter((row) => matchesSearch(row, filters.search)), [rows, filters.search]);
    const selectableIds = useMemo(() => new Set(visibleRows.filter((row) => row.document).map((row) => row._id)), [visibleRows]);
    const selectedIds = useMemo(() => selectionModel.filter((id) => selectableIds.has(id)), [selectionModel, selectableIds]);
    const sampleRow = useMemo(() => {
        const chosen = new Set(selectedIds);
        return visibleRows.find((row) => chosen.has(row._id));
    }, [visibleRows, selectedIds]);
    const busy = generating || downloading;
    const isRowSelectable = useCallback(({ row }) => !!row.document && !busy, [busy]);

    const handleFilterChange = useCallback((changes) => setFilters((previous) => ({ ...previous, ...changes })), []);
    const handleReset = () => setFilters(getDefaultFilters());

    const handleGenerate = async (row) => {
        const result = await confirmAsync({
            title: row.document ? 'Regenerate Delivery Report' : 'Generate Delivery Report',
            message: row.document ? 'A new version will replace the current document for this route session. Previous versions are retained.' : 'Generate a delivery report for this route session using its current data?',
            asyncFn: () => generate({ id: row._id }),
        });
        if (result === false) return;
        if (result instanceof Error) {
            showSnackbar({ severity: 'error', message: result.message });
            return;
        }
        showSnackbar({ severity: 'success', message: `Delivery report ${row.document ? 'regenerated' : 'generated'} for ${row.BOL ?? 'this route session'}` });
    };

    const handleDownloadRow = async (row) => {
        try {
            startDownload(await getFileURL(`reports/${row.document.file}`, true, deliveryDocumentFilename(row), true));
        } catch (err) {
            showSnackbar({ severity: 'error', message: err.message });
        }
    };

    const handleViewVersion = (version) => setViewing({ ...versionsFor, document: { ...versionsFor.document, file: version.file, version: version.version } });

    const handleDownloadVersion = async (version) => {
        try {
            startDownload(await getFileURL(`reports/${version.file}`, true, deliveryDocumentVersionFilename(versionsFor, version.version), true));
        } catch (err) {
            showSnackbar({ severity: 'error', message: err.message });
        }
    };

    const handleDownload = () => {
        if (selectedIds.length > MAX_ZIP_DOCUMENTS) {
            showSnackbar({ severity: 'error', message: `Select up to ${MAX_ZIP_DOCUMENTS} reports per download. Narrow the filters or the selection and try again.` });
            return;
        }
        setLayout([]);
        setFolderDialogOpen(true);
    };

    const handleCancelDownload = () => {
        download.cancel();
        showSnackbar({ severity: 'info', message: 'Download cancelled. Your browser may show the file as failed.' });
    };

    const handleStartDownload = (chosenLayout) => {
        setFolderDialogOpen(false);
        download.start(selectedIds, chosenLayout);
    };

    const columns = useMemo(() => getDeliveryDocumentColumns({ localization, onInspect: (row) => history.push(`${inspectRouteSessionSlug}/${row._id}`), onView: setViewing, onDownload: handleDownloadRow, onHistory: setVersionsFor, onGenerate: handleGenerate, busy }), [localization, busy]);

    return (
        <TitledPage title="Delivery Documents" sx={{ p: { xs: 2, md: 3 } }}>
            <DeliveryDocumentFilters filters={filters} onChange={handleFilterChange} onReset={handleReset} onRefresh={() => refetch()} disabled={busy} refreshing={isFetching} />
            {isError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    Unable to load delivery documents. Check the filters and refresh.
                </Alert>
            )}
            <Table
                sx={baseTableSx}
                rows={visibleRows}
                columns={columns}
                getRowId={(row) => row._id}
                loading={isLoading}
                pagination
                rowsPerPageOptions={[25, 50, 100]}
                initialState={{ pagination: { pageSize: 25 } }}
                checkboxSelection
                disableSelectionOnClick
                keepNonExistentRowsSelected
                isRowSelectable={isRowSelectable}
                selectionModel={selectionModel}
                onSelectionModelChange={setSelectionModel}
                components={{ Toolbar: DefaultToolbar }}
                componentsProps={{
                    toolbar: {
                        ...baseToolbarProps,
                        searchable: false,
                        exportable: false,
                        title: { enabled: true, inLine: true, text: `${visibleRows.length} ${visibleRows.length === 1 ? 'session' : 'sessions'}` },
                        actions: [{ title: 'Download ZIP', fullTitle: `Download ZIP (${selectedIds.length})`, disabled: busy || selectedIds.length === 0, onClick: handleDownload }],
                    },
                }}
                localeText={{ noRowsLabel: 'No delivered or in-progress route sessions match these filters.' }}
            />
            <ConfirmationDialog />
            <Prompt when={downloading} message="A delivery report download is in progress. If you leave, the file keeps downloading in your browser but you will not see its progress here. Leave anyway?" />
            <DeliveryReportsFolderDialog open={folderDialogOpen} count={selectedIds.length} layout={layout} sampleRow={sampleRow} onLayoutChange={setLayout} onCancel={() => setFolderDialogOpen(false)} onDownload={handleStartDownload} />
            <DeliveryReportsDownloadDialog state={download.state} onRetry={download.retry} onCancel={handleCancelDownload} onClose={download.close} />
            <DeliveryReportHistoryDialog row={versionsFor} onClose={() => setVersionsFor(null)} onView={handleViewVersion} onDownload={handleDownloadVersion} />
            <DocumentViewerDialog open={!!viewing} onClose={() => setViewing(null)} title={viewing ? documentTitle(viewing) : ''} subtitle={viewing?.route_id?.name} file={viewing?.document?.file} />
        </TitledPage>
    );
};

const DeliveryDocumentsApp = () => {
    const hasPermission = userHasPermission(DELIVERY_DOCUMENTS_PERMISSION);
    return hasPermission ? <DeliveryDocumentsPage /> : <RedirectGuard hasPermission={false} />;
};

export default DeliveryDocumentsApp;
